import BookingsHelpFlexPane from './HelpFlexPane';
import renderFeedbackPanel from './Feedback';
import {
    headerContainer,
    hidden,
    myBookingsAppLogo,
    horizontalContainer,
    buttonRoot,
    buttonTextContainer,
    iconColor,
    myBookingsButton,
    myBookingsButtonText,
} from './BookWithMeSuiteHeader.scss';

import createThemeData from '../utils/createThemeData';
import { createHelpLink, flexPaneVisiblityChanged } from '../utils/miscUtils';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import '@1js/suiteux-shell';
import classNames from 'owa-classnames';
import { isSupportEnabled, lazyHandleSupportPaneEvents } from 'diagnostics-and-support';
import { observer } from 'owa-mobx-react';
import { getBposNavBarData } from 'owa-bpos-store';
import { getLogicalRing } from 'owa-config';
import { isAnonymousUser } from 'bookings-shared/lib/c2/utils/isAnonymousUser';
import { ControlIconsDefinitions } from 'owa-control-icons/lib/data/controlIconsRegistration';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyHandleSocPostMessageEvents } from 'owa-help-charm';
import { feedbackLink } from 'owa-locstrings/lib/strings/feedbacklink.locstring.json';
import { helpLink } from 'owa-locstrings/lib/strings/helplink.locstring.json';
import { privacyLink } from 'owa-locstrings/lib/strings/privacylink.locstring.json';
import { termsLink } from 'owa-locstrings/lib/strings/termslink.locstring.json';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';
import { getResourcePathUrl } from 'owa-resource-url';
import { getConfig } from 'owa-service/lib/config';
import { isConsumer } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { OwaShellHelpButton } from 'owa-suite-header/lib/components/OwaSuiteHeaderButton';
import { ThemeConstants } from 'owa-theme-shared';
import { lazyLaunchInAppFeedback } from 'owa-uservoice';
import React from 'react';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Avoid deprecated APIs, use portals instead of rendering to a new root
 *	> 'render' import from 'owa-react-dom' is restricted. Use react portals instead of rendering into a new root */
import { render } from 'owa-react-dom';
import type {
    BaseTheme as ShellThemePalette,
    ShellControl,
    ShellLayout,
    CustomizationRegion,
    NavBarData as ShellNavBarData,
    NavBarLinkData,
} from '@1js/suiteux-shell-react';
import { SuiteHeader } from '@1js/suiteux-shell-react';
import loc, {
    getCurrentLanguage,
    getCurrentCulture,
    isCurrentCultureRightToLeft,
} from 'owa-localize';
import {
    bookWithMeFeedbackButtonAriaLabel,
    bookWithMeManageBookingButtonLabel,
    bookWithMeAppIconAriaLabel,
} from './Shared.locstring.json';
import BookingButton from './BookingButton';
import { Icon, ImageIcon } from '@fluentui/react/lib/Icon';
import CalendarPersonFilled from 'owa-fluent-icons-svg/lib/icons/CalendarPersonFilled';
import EmojiFilled from 'owa-fluent-icons-svg/lib/icons/EmojiFilled';
import BookingsLogo from '../assets/BookingsLogo.svg';
import { bookingsUrl } from 'bookwithme-shared';

const owaIconFontFamily = 'controlIcons';
const BOOKING_DASHBOARD_ROUTE = '/bookwithme/me';
const BRAND_ENTRYPOINT = '?ep=bwmbrand';
const SHELL_BOOKWITHME_ID = 'ShellBookWithMe';

interface BookWithMeSuiteHeaderProps {
    isOrganizerView?: boolean;
    showHeader?: boolean;
}

export default observer(function BookWithMeSuiteHeader(props: BookWithMeSuiteHeaderProps) {
    const isOrganizerView = !!props.isOrganizerView;
    useWindowEvent(
        'message',
        (messageEvent: any) => {
            lazyHandleSocPostMessageEvents.importAndExecute(messageEvent);
            if (isSupportEnabled()) {
                lazyHandleSupportPaneEvents.importAndExecute(messageEvent);
            }
        },
        'BookWithMeSuiteHeader'
    );

    const feedbackNavLink = createHelpLink(
        'BookWithMeFeedbackLink',
        loc(feedbackLink),
        undefined,
        () => {
            lazyLaunchInAppFeedback.importAndExecute();
            return false;
        }
    );

    const privacyNavLink = createHelpLink(
        'BookWithMePrivacyLink',
        loc(privacyLink),
        'https://go.microsoft.com/fwlink/?linkId=521839'
    );

    const legalNavLink = createHelpLink(
        'BookWithMeTermsLink',
        loc(termsLink),
        'https://www.microsoft.com/en-us/servicesagreement'
    );

    /**
     * When the UserThemeId is set to 'base', the Shell uses an AppBrandTheme palette, if provided.
     * This will be taken from the OWA theme definition with Id 'base'.
     */
    const owaBrandTheme = React.useRef<ShellThemePalette>(
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2769 (112,9): No overload matches this call.
        // @ts-expect-error
        createThemeData(ThemeConstants.BASE_THEME_ID).UserThemePalette
    );

    const isPersonalBookingEnabled: boolean =
        !isAnonymousUser() &&
        isFeatureEnabled('cal-personalbookingpage') &&
        isFeatureEnabled('eventify-booking');

    const isMsitUser =
        !isAnonymousUser() && (getLogicalRing() === 'Dogfood' || getLogicalRing() === 'Microsoft');

    // Help link titles
    const helpLinkTitle = loc(helpLink);
    const feedbackTitle = loc(feedbackLink);
    // Links
    const bookwithmeHelpLink =
        'https://docs.microsoft.com/en-us/microsoft-365/bookings/bookings-in-outlook?view=o365-worldwide';
    // Link components
    const helpNavLink = createHelpLink('HelpLink', helpLinkTitle, bookwithmeHelpLink);

    const myBookingsLogo = createChildControlFromCharm(
        'MyBookingsLogo',
        getMyBookingsLogo(),
        '',
        loc(bookWithMeAppIconAriaLabel),
        ''
    );

    const smileButtonText = loc(bookWithMeFeedbackButtonAriaLabel);
    const SmileButton = createChildControlFromCharm(
        'SmileButton',
        getSmileButton(),
        ControlIconsDefinitions.Emoji2,
        smileButtonText,
        smileButtonText
    );

    const bookingButtonText = loc(bookWithMeManageBookingButtonLabel);
    /* eslint-disable-next-line @typescript-eslint/no-shadow  -- (https://aka.ms/OWALintWiki)
     * See https://typescript-eslint.io/rules/no-shadow
     *	> 'BookingButton' is already declared in the upper scope. */
    const BookingButton = createChildControlFromCharm(
        'BookingButton',
        getBookingButton(bookingButtonText),
        ControlIconsDefinitions.BookingsLogo,
        bookingButtonText,
        bookingButtonText
    );

    const BookingsButton = createChildControlFromCharm(
        'BookingsButton',
        getBookingsButton(),
        ControlIconsDefinitions.BookingsLogo,
        '',
        ''
    );
    const isShowManageBookingButton = isPersonalBookingEnabled && !isOrganizerView;

    const getNavBarData = (): ShellNavBarData | undefined => {
        if (isAnonymousUser()) {
            return undefined;
        }

        const owaNavBarData: ShellNavBarData = getBposNavBarData() as ShellNavBarData;
        if (owaNavBarData) {
            const helpSubLink: NavBarLinkData[] = [];

            helpSubLink.push(
                {
                    Id: 'BookingsCustomHelpLink',
                    TargetWindow: '_blank',
                    Text: helpLinkTitle,
                    Title: helpLinkTitle,
                    Url: bookwithmeHelpLink,
                },
                {
                    Id: 'BookWithMeCustomFeedbackLink',
                    Text: feedbackTitle,
                    Title: feedbackTitle,
                    Action: () => {
                        lazyLaunchInAppFeedback.importAndExecute();

                        return false;
                    },
                    Url: '#',
                }
            );

            const navBarData: ShellNavBarData = {
                ...owaNavBarData,
                CurrentMainLinkElementID: SHELL_BOOKWITHME_ID,
                ThemeDataOverride: createThemeData(),
                AppBrandTheme: owaBrandTheme.current,
                FeedbackLink: feedbackNavLink,
                HelpLink: helpNavLink,
                PrivacyLink: privacyNavLink,
                LegalLink: legalNavLink,
                CurrentWorkloadHelpSubLinks: helpSubLink,
            };
            return navBarData;
        } else {
            return undefined;
        }
    };

    const createCustomLayout = (): ShellLayout => {
        const customHeaderButtons: ShellControl[] = [];
        pushOnCondition(customHeaderButtons, BookingsButton, isOrganizerView);
        pushOnCondition(customHeaderButtons, BookingButton, isShowManageBookingButton);
        pushOnCondition(customHeaderButtons, SmileButton, isMsitUser);
        pushOnCondition(customHeaderButtons, OwaShellHelpButton);

        // Now define responsive behavior for all the custom header buttons, with the
        // right-most one getting dropped first
        customHeaderButtons.forEach((control, index, array) => {
            if (!control.responsiveBehavior) {
                control.responsiveBehavior = {
                    responsivePriority: array.length - 1 - index + 2,
                    minimizeBehavior: 'overflow',
                    delayMinimize: false,
                };
            } else if (control.responsiveBehavior.responsivePriority == 0) {
                // Update the responsive priorities for the buttons as per its position in the collection
                control.responsiveBehavior.responsivePriority = array.length - 1 - index + 2;
            }
        });

        const brandingAdditions: ShellControl[] = [];

        const layout = {
            leftCustomizationRegion: {
                includeAppLauncher: false,
            },
            centerCustomizationRegion: {
                flex: '1 0 auto',
                minWidth: '300px',
                children: [
                    {
                        regionID: 'brandingBlock',
                        minWidth: 'auto',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        flex: '1 0 auto',
                        children: [
                            myBookingsLogo,
                            {
                                id: 'bookwithmeBranding',
                                nativeControlID: 'O365Branding',
                            } as ShellControl,
                            ...brandingAdditions,
                        ],
                    } as CustomizationRegion,
                    {
                        /* Menu Buttons - Right aligned */
                        overflowX: 'auto',
                        regionID: 'testlinkblock',
                        flex: '0 0 auto',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        minWidth: 'auto',
                        children: [...customHeaderButtons],
                    } as CustomizationRegion,
                ],
            } as CustomizationRegion,
            rightCustomizationRegion: {
                includeMeControl: true,
            },
            flexPaneCollection: [BookingsHelpFlexPane],
            enableResponsive: true,
        };
        return layout;
    };

    return (
        <div className={classNames(headerContainer, { [hidden]: !props.showHeader })}>
            <SuiteHeader
                culture={getCurrentCulture()}
                language={getCurrentLanguage() || 'en'}
                isRTL={isCurrentCultureRightToLeft()}
                userDisplayName={getUserConfiguration().SessionSettings?.UserDisplayName}
                userPrincipalName={getUserConfiguration().SessionSettings?.UserPrincipalName}
                workloadID={'Exchange'}
                appBrandTheme={createThemeData().UserThemePalette}
                currentMainLinkElementID={SHELL_BOOKWITHME_ID}
                themeData={createThemeData()}
                disableToasts={true} // This will turn off suite header toasts
                userID={getUserConfiguration().SessionSettings?.UserPuid}
                isConsumer={isConsumer()}
                navBarData={getNavBarData()}
                shellAssetsContainerOverride={`${getResourcePathUrl('suiteux-shell')}`}
                onFlexPaneVisibilityChanged={flexPaneVisiblityChanged}
                /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                 * Causes extra re renders of component
                 *	> JSX attribute values should not contain objects created in the same scope */
                shellDataOverrides={{
                    SuiteServiceUrl: `${window.location.origin}${getConfig().baseUrl}/service.svc`,
                    AppHeaderLinkText: 'Bookings with me',
                    AppHeaderLinkUrl: isOrganizerView
                        ? `${window.location.origin}${BOOKING_DASHBOARD_ROUTE}${window.location.search}`
                        : isPersonalBookingEnabled
                        ? `${window.location.origin}${BOOKING_DASHBOARD_ROUTE}${BRAND_ENTRYPOINT}`
                        : undefined,
                    // TODO: Find a better way of not having the inbuilt iframe try and redirect if anonymous.
                    MAXHelpEnabled: !isAnonymousUser(),
                    FeedbackLink: !isAnonymousUser() ? feedbackNavLink : undefined,
                    HelpLink: helpNavLink,
                    SocHelpUrl: isConsumer()
                        ? 'https://support.office.live.com'
                        : 'https://support.office.com',
                    // DO NOT OVERRIDE PrivacyLink and LegalLink here.
                }}
                customLayout={createCustomLayout()}
            />
        </div>
    );
}, 'BookWithMeSuiteHeader');

function getMyBookingsLogo() {
    return (
        <div className={myBookingsAppLogo}>
            <Icon iconName={CalendarPersonFilled} />
        </div>
    );
}

function getSmileButton() {
    return (
        <div
            /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
             * Causes extra re renders of component
             *	> JSX attribute values should not contain objects created in the same scope */
            style={{ display: 'flex', height: '100%' }}
            className={classNames(horizontalContainer)}
        >
            <CommandBarButton
                /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                 * Causes extra re renders of component
                 *	> JSX attribute values should not contain objects created in the same scope */
                styles={{
                    root: buttonRoot,
                    textContainer: classNames(buttonTextContainer),
                }}
                title={loc(bookWithMeFeedbackButtonAriaLabel)}
                aria-label={loc(bookWithMeFeedbackButtonAriaLabel)}
                onClick={renderFeedbackPanel}
            >
                <Icon className={iconColor} iconName={EmojiFilled} />
            </CommandBarButton>
        </div>
    );
}

function getBookingsButton() {
    /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
     * Baseline, please do not copy and paste this justification
     *	> JSX attribute values should not contain functions created in the same scope */
    const renderIcon = () => (
        <ImageIcon
            /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
             * Causes extra re renders of component
             *	> JSX attribute values should not contain objects created in the same scope */
            imageProps={{
                src: BookingsLogo,
                alt: 'Bookings',
            }}
        />
    );
    /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
     * Baseline, please do not copy and paste this justification
     *	> JSX attribute values should not contain functions created in the same scope */
    const onBookingsClick = () => {
        window.open(bookingsUrl, '_blank');
    };
    return (
        <div className={classNames(horizontalContainer)}>
            <CommandBarButton
                onRenderIcon={renderIcon}
                /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                 * Causes extra re renders of component
                 *	> JSX attribute values should not contain objects created in the same scope */
                styles={{
                    root: buttonRoot,
                    icon: iconColor,
                    textContainer: classNames(
                        buttonTextContainer,
                        myBookingsButton,
                        myBookingsButtonText
                    ),
                }}
                text={'Bookings'}
                title={'Bookings'}
                aria-label={'Bookings'}
                onClick={onBookingsClick}
            />
        </div>
    );
}

function getBookingButton(buttonText: string) {
    return <BookingButton buttonText={buttonText} />;
}

function pushOnCondition(
    controlCollection: ShellControl[],
    control: ShellControl,
    condition?: boolean
) {
    if (condition === undefined || condition) {
        controlCollection.push(control);
    }
}

function createChildControlFromCharm(
    key: React.Key,
    charm: JSX.Element,
    iconCode: string,
    ariaLabel: string,
    affordanceMenuItemText: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7051 (392,15): Parameter has a name but no type. Did you mean 'arg0: ClientRect'? ...
    // @ts-expect-error
    onShow?: (ClientRect) => void,
    onHide?: () => void
): ShellControl {
    const containerRef = React.createRef<HTMLDivElement>();

    return {
        id: key,
        render: (container: HTMLDivElement) =>
            render(<div ref={containerRef}>{charm}</div>, container),
        responsiveBehavior: {
            responsivePriority: 0,
            responsiveSteps: [16],
            minimizeBehavior: 'overflow',
            affordanceBehavior: {
                customButtonRenderData: {
                    id: key,
                    iconFontName: iconCode,
                    iconFontFamily: owaIconFontFamily,
                    onHide,
                    onShow,
                    ariaLabel,
                    affordanceMenuItemText,
                },
            },
        },
    } as ShellControl;
}
